/**
 * Web Vitals - Performance Monitoring pour CareWashGo
 * Mesure LCP, FID, CLS, FCP, TTFB
 * Note: Ce module n'est actif que sur le web, pas sur les apps natives
 */

import { IS_NATIVE } from './http';
import { safeLocalStorage, safeJsonParse } from './safeUtils';

// Stockage des métriques
const metrics = {
  LCP: null,  // Largest Contentful Paint
  FID: null,  // First Input Delay
  CLS: null,  // Cumulative Layout Shift
  FCP: null,  // First Contentful Paint
  TTFB: null, // Time to First Byte
  INP: null,  // Interaction to Next Paint
};

const observers = [];

/**
 * Envoyer les métriques au backend (optionnel)
 */
async function sendMetrics(metric) {
  // Ne pas stocker les métriques sur les apps natives
  if (IS_NATIVE) return;
  
  try {
    // Stocker localement
    const storedStr = safeLocalStorage.getItem('webVitals');
    const stored = safeJsonParse(storedStr, []);
    stored.push({
      ...metric,
      timestamp: Date.now(),
      url: window.location.pathname,
      userAgent: navigator.userAgent
    });
    
    // Garder seulement les 100 dernières entrées
    if (stored.length > 100) stored.shift();
    safeLocalStorage.setItem('webVitals', JSON.stringify(stored));
    
    // Log en dev
    if (process.env.NODE_ENV === 'development') {
    }
  } catch (error) {
    console.warn('[WebVitals] Error storing metrics:', error);
  }
}

/**
 * Observer pour Largest Contentful Paint (LCP)
 */
function observeLCP() {
  if (!('PerformanceObserver' in window)) return;
  
  try {
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1];
      
      metrics.LCP = lastEntry.startTime;
      sendMetrics({
        name: 'LCP',
        value: lastEntry.startTime,
        unit: 'ms',
        rating: lastEntry.startTime <= 2500 ? 'good' : lastEntry.startTime <= 4000 ? 'needs-improvement' : 'poor'
      });
    });
    
    observer.observe({ type: 'largest-contentful-paint', buffered: true });
    observers.push(observer);
  } catch (e) {
    console.warn('[WebVitals] LCP not supported');
  }
}

/**
 * Observer pour First Input Delay (FID)
 */
function observeFID() {
  if (!('PerformanceObserver' in window)) return;
  
  try {
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const firstInput = entries[0];
      
      if (firstInput) {
        const fid = firstInput.processingStart - firstInput.startTime;
        metrics.FID = fid;
        sendMetrics({
          name: 'FID',
          value: fid,
          unit: 'ms',
          rating: fid <= 100 ? 'good' : fid <= 300 ? 'needs-improvement' : 'poor'
        });
      }
    });
    
    observer.observe({ type: 'first-input', buffered: true });
    observers.push(observer);
  } catch (e) {
    console.warn('[WebVitals] FID not supported');
  }
}

/**
 * Observer pour Cumulative Layout Shift (CLS)
 */
function observeCLS() {
  if (!('PerformanceObserver' in window)) return;
  
  let clsValue = 0;
  let clsEntries = [];
  let sessionValue = 0;
  let sessionEntries = [];
  
  try {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (!entry.hadRecentInput) {
          const firstSessionEntry = sessionEntries[0];
          const lastSessionEntry = sessionEntries[sessionEntries.length - 1];
          
          if (sessionValue && 
              entry.startTime - lastSessionEntry.startTime < 1000 &&
              entry.startTime - firstSessionEntry.startTime < 5000) {
            sessionValue += entry.value;
            sessionEntries.push(entry);
          } else {
            sessionValue = entry.value;
            sessionEntries = [entry];
          }
          
          if (sessionValue > clsValue) {
            clsValue = sessionValue;
            clsEntries = sessionEntries;
            
            metrics.CLS = clsValue;
            sendMetrics({
              name: 'CLS',
              value: clsValue,
              unit: '',
              rating: clsValue <= 0.1 ? 'good' : clsValue <= 0.25 ? 'needs-improvement' : 'poor'
            });
          }
        }
      }
    });
    
    observer.observe({ type: 'layout-shift', buffered: true });
    observers.push(observer);
  } catch (e) {
    console.warn('[WebVitals] CLS not supported');
  }
}

/**
 * Observer pour First Contentful Paint (FCP)
 */
function observeFCP() {
  if (!('PerformanceObserver' in window)) return;
  
  try {
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const fcp = entries.find(e => e.name === 'first-contentful-paint');
      
      if (fcp) {
        metrics.FCP = fcp.startTime;
        sendMetrics({
          name: 'FCP',
          value: fcp.startTime,
          unit: 'ms',
          rating: fcp.startTime <= 1800 ? 'good' : fcp.startTime <= 3000 ? 'needs-improvement' : 'poor'
        });
      }
    });
    
    observer.observe({ type: 'paint', buffered: true });
    observers.push(observer);
  } catch (e) {
    console.warn('[WebVitals] FCP not supported');
  }
}

/**
 * Mesurer Time to First Byte (TTFB)
 */
function measureTTFB() {
  if (!('performance' in window)) return;
  
  const navigation = performance.getEntriesByType('navigation')[0];
  if (navigation) {
    const ttfb = navigation.responseStart - navigation.requestStart;
    metrics.TTFB = ttfb;
    sendMetrics({
      name: 'TTFB',
      value: ttfb,
      unit: 'ms',
      rating: ttfb <= 800 ? 'good' : ttfb <= 1800 ? 'needs-improvement' : 'poor'
    });
  }
}

/**
 * Observer pour Interaction to Next Paint (INP)
 */
function observeINP() {
  if (!('PerformanceObserver' in window)) return;
  
  let maxINP = 0;
  
  try {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.interactionId) {
          const duration = entry.duration;
          if (duration > maxINP) {
            maxINP = duration;
            metrics.INP = maxINP;
            sendMetrics({
              name: 'INP',
              value: maxINP,
              unit: 'ms',
              rating: maxINP <= 200 ? 'good' : maxINP <= 500 ? 'needs-improvement' : 'poor'
            });
          }
        }
      }
    });
    
    observer.observe({ type: 'event', buffered: true, durationThreshold: 16 });
    observers.push(observer);
  } catch (e) {
    console.warn('[WebVitals] INP not supported');
  }
}

/**
 * Initialiser tous les observers
 */
export function initWebVitals() {
  // Ne pas initialiser sur les apps natives
  if (IS_NATIVE) {
    return;
  }
  
  if (typeof window === 'undefined') return;
  
  // Attendre que la page soit chargée
  if (document.readyState === 'complete') {
    startObservers();
  } else {
    window.addEventListener('load', startObservers);
  }
}

function startObservers() {
  observeLCP();
  observeFID();
  observeCLS();
  observeFCP();
  observeINP();
  measureTTFB();
  
}

/**
 * Obtenir les métriques actuelles
 */
export function getMetrics() {
  return { ...metrics };
}

/**
 * Obtenir l'historique des métriques
 */
export function getMetricsHistory() {
  if (IS_NATIVE) return [];
  const storedStr = safeLocalStorage.getItem('webVitals');
  return safeJsonParse(storedStr, []);
}

/**
 * Obtenir un résumé des performances
 */
export function getPerformanceSummary() {
  const history = getMetricsHistory();
  
  if (history.length === 0) return null;
  
  const summary = {
    LCP: { avg: 0, count: 0, good: 0, poor: 0 },
    FID: { avg: 0, count: 0, good: 0, poor: 0 },
    CLS: { avg: 0, count: 0, good: 0, poor: 0 },
    FCP: { avg: 0, count: 0, good: 0, poor: 0 },
    TTFB: { avg: 0, count: 0, good: 0, poor: 0 },
    INP: { avg: 0, count: 0, good: 0, poor: 0 }
  };
  
  history.forEach(m => {
    if (summary[m.name]) {
      summary[m.name].avg += m.value;
      summary[m.name].count++;
      if (m.rating === 'good') summary[m.name].good++;
      if (m.rating === 'poor') summary[m.name].poor++;
    }
  });
  
  // Calculer les moyennes
  Object.keys(summary).forEach(key => {
    if (summary[key].count > 0) {
      summary[key].avg = summary[key].avg / summary[key].count;
    }
  });
  
  return summary;
}

/**
 * Effacer l'historique
 */
export function clearMetricsHistory() {
  if (IS_NATIVE) return;
  safeLocalStorage.removeItem('webVitals');
}

/**
 * Arrêter tous les observers
 */
export function stopWebVitals() {
  observers.forEach(obs => obs.disconnect());
  observers.length = 0;
}

export default {
  initWebVitals,
  getMetrics,
  getMetricsHistory,
  getPerformanceSummary,
  clearMetricsHistory,
  stopWebVitals
};
